import { describeCoveredUnits } from '@/lib/hqlFormat';
import { checkHql, evaluateHql, type HomeDocument, type HqlCatalog, type HqlEvaluation } from '@rock/hql';
import type { ConsiderationType, SpaceId, TaskType } from '@rock/types';
import { considerationCadence } from '@rock/util';
import { orderBy } from 'lodash';

export type DryRunOutcome = HqlEvaluation | 'invalid' | 'mixed';

export type DryRunRow = {
    key: string;
    type: ConsiderationType;
    query: string;
    /** The space its query waits on, besides the ones it reads (the consideration's own). */
    spaceId: SpaceId | undefined;
    isDraft: boolean;
    outcome: DryRunOutcome;
};

/**
 * Every approved type's query, and its waiting draft, run against one home the way evaluation runs
 * them: approved types only, each query waiting on its type's own space, and a type that mixes
 * one-time and recurring tasks not applied at all.
 */
export function dryRun(
    types: ConsiderationType[],
    taskTypesByType: Record<string, TaskType[]>,
    doc: HomeDocument,
    catalog: HqlCatalog,
    now: Date,
): DryRunRow[] {
    const rows = types
        .filter((type) => type.documentStatus === 'approved')
        .flatMap((type) => {
            const mixed = considerationCadence(taskTypesByType[type.id] ?? []) === 'mixed';
            const row = (query: string, isDraft: boolean): DryRunRow => ({
                key: `${type.id}:${isDraft ? 'draft' : 'approved'}`,
                type,
                query,
                spaceId: type.spaceId,
                isDraft,
                outcome: mixed ? 'mixed' : evaluate(query, type.spaceId, doc, catalog, now),
            });
            const draftQuery = type.appliesToDraft?.query;
            return [
                ...(type.appliesTo ? [row(type.appliesTo, false)] : []),
                ...(draftQuery && draftQuery !== type.appliesTo ? [row(draftQuery, true)] : []),
            ];
        });
    return orderBy(rows, [(row) => row.type.title.toLowerCase(), (row) => row.isDraft]);
}

function evaluate(
    query: string,
    spaceId: SpaceId | undefined,
    doc: HomeDocument,
    catalog: HqlCatalog,
    now: Date,
): DryRunOutcome {
    const { compiled } = checkHql(query, catalog, { spaceId });
    return compiled ? evaluateHql(compiled, doc, now) : 'invalid';
}

/** The covered units by type ("2 × mini-split, heat-pump"), or what the query waits on. */
export function describeCovers(outcome: DryRunOutcome, doc: HomeDocument): string {
    if (typeof outcome === 'string' || outcome.status === 'does-not-apply') return '';
    if (outcome.status === 'pending') return `waits for ${outcome.missingSpaceIds.join(', ')}`;
    return describeCoveredUnits(outcome.covers, doc.equipment);
}
